<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const PANEL_KEY = '69cc189637de1fe6455ab233504606758d2c9f3a06388e84';

// Beden sıralaması (küçükten büyüğe). XXL ve 2XL aynı kabul edilir.
const SIZE_ORDER = [
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
  '7XL',
  '8XL',
];

function sizeIndex(beden) {
  const i = SIZE_ORDER.indexOf(beden);
  return i === -1 ? 999 : i;
}

// Zebra sütun arka planı — bir açık bir koyu, gözle takibi kolaylaştırır
function colBg(idx) {
  return idx % 2 === 1 ? 'bg-slate-100/70 dark:bg-slate-700/30' : '';
}

const route = useRoute();
const router = useRouter();

const product = ref(null);
const costs = ref({});
const loading = ref(true);
const error = ref('');
const selectedImage = ref('');
const selectedColor = ref(''); // colorCode key
const selectedSize = ref('');
const costInput = ref('');
const saving = ref(false);
const saved = ref(false);
const shopifyData = ref(null);
const ticimaxData = ref(null); // null=loading, 'not_found', or {minSell,maxSell,minList,maxList}
const modaxTrendyolData = ref(null); // null=loading, 'not_found', or {minSell,maxSell,minList,maxList}

// ── Computed ───────────────────────────────────────────────────────────────

const colorGroups = computed(() => product.value?.colorGroups || []);

const selectedGroup = computed(
  () =>
    colorGroups.value.find(g => g.colorCode === selectedColor.value) ??
    colorGroups.value[0] ??
    null
);

// ALL images for the currently selected color — shown in thumbnail strip
const currentImages = computed(() => selectedGroup.value?.images || []);

const sizesForColor = computed(() =>
  (selectedGroup.value?.variants || []).sort(
    (a, b) =>
      sizeIndex(a.beden) - sizeIndex(b.beden) || a.beden.localeCompare(b.beden)
  )
);

// Renk (satır) × Beden (sütun) stok matrisi — operasyonel stok görünümü
const stockMatrix = computed(() => {
  const groups = colorGroups.value;
  if (!groups.length) return null;

  const sizeSet = new Set();
  groups.forEach(g => g.variants.forEach(v => sizeSet.add(v.beden)));
  const sizes = [...sizeSet].sort(
    (a, b) => sizeIndex(a) - sizeIndex(b) || a.localeCompare(b)
  );

  const rows = groups.map(g => {
    const cells = {};
    sizes.forEach(s => {
      cells[s] = 0;
    });
    let total = 0;
    g.variants.forEach(v => {
      cells[v.beden] = (cells[v.beden] || 0) + v.stok;
      total += v.stok;
    });
    return {
      colorCode: g.colorCode,
      colorName: g.colorName,
      cells,
      total,
    };
  });

  const colTotals = {};
  sizes.forEach(s => {
    colTotals[s] = rows.reduce((sum, r) => sum + (r.cells[s] || 0), 0);
  });
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0);

  return { sizes, rows, colTotals, grandTotal };
});

const selectedVariant = computed(
  () => sizesForColor.value.find(v => v.beden === selectedSize.value) ?? null
);

const totalStock = computed(() =>
  colorGroups.value.reduce(
    (s, g) => s + g.variants.reduce((a, v) => a + v.stok, 0),
    0
  )
);

const productKey = computed(() => product.value?.id || '');

const savedCost = computed(
  () => costs.value[productKey.value]?.maliyet ?? null
);

// $ bazli toplu ice aktarilan maliyetler icin kur/KDV dokumu — TCMB kuru
// 'Marketplace::RefreshMaliyetExchangeRateJob' tarafindan periyodik
// guncellenir (her 3 saatte bir kontrol, TCMB gunde ~1 kez degisir).
const costBreakdown = computed(() => {
  const entry = costs.value[productKey.value];
  if (!entry?.maliyetUsd) return null;
  return {
    usd: entry.maliyetUsd,
    kdvOrani: entry.kdvOrani,
    kur: entry.kurKullanilan,
    kurTarihi: entry.kurTarihi,
  };
});

// Trendyol price: from selected group or overall range
const trendyolPrice = computed(() => {
  if (!product.value) return null;
  const g = selectedGroup.value;
  if (g) {
    const prices = g.variants.map(v => v.salePrice).filter(p => p > 0);
    const lists = g.variants.map(v => v.listPrice).filter(p => p > 0);
    const sell = prices.length ? Math.min(...prices) : null;
    const list = lists.length ? Math.max(...lists) : null;
    return {
      sell: sell ? `${sell.toFixed(2)} ₺` : null,
      list: list && sell && list > sell ? `${list.toFixed(2)} ₺` : null,
    };
  }
  const min = product.value.minSalePrice;
  const max = product.value.maxSalePrice;
  const list = product.value.maxListPrice;
  return {
    sell:
      min === max
        ? `${min.toFixed(2)} ₺`
        : `${min.toFixed(2)} – ${max.toFixed(2)} ₺`,
    list: list && list > max ? `${list.toFixed(2)} ₺` : null,
  };
});

const profitData = computed(() => {
  if (savedCost.value === null || !product.value) return null;
  const prices = colorGroups.value
    .flatMap(g => g.variants.map(v => v.salePrice))
    .filter(p => p > 0);
  if (!prices.length) return null;
  const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
  const profit = avg - savedCost.value;
  return {
    avgPrice: avg,
    cost: savedCost.value,
    profit,
    margin: (profit / avg) * 100,
  };
});

// ── Actions ────────────────────────────────────────────────────────────────

function selectColor(colorCode) {
  selectedColor.value = colorCode;
  selectedSize.value = '';
  const group = colorGroups.value.find(g => g.colorCode === colorCode);
  selectedImage.value = group?.images?.[0] || '';
}

function goBack() {
  router.push({
    name: 'catalog_index',
    params: { accountId: route.params.accountId },
  });
}

async function saveCost() {
  const maliyetUsd = parseFloat(costInput.value);
  if (Number.isNaN(maliyetUsd) || maliyetUsd < 0) return;
  saving.value = true;
  saved.value = false;
  try {
    const res = await fetch('/ticimax-api/maliyet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Panel-Key': PANEL_KEY },
      body: JSON.stringify({
        stokKodu: productKey.value,
        maliyetUsd,
        urunAdi: product.value?.title,
      }),
    });
    const data = await res.json();
    if (data.success) {
      costs.value[productKey.value] = data.entry || {
        maliyetUsd,
        urunAdi: product.value?.title,
      };
      saved.value = true;
    }
  } finally {
    saving.value = false;
  }
}

function baseSku() {
  // product.id is the Trendyol productMainId which equals base SKU (e.g. ADR-1297W122TSH1)
  return product.value?.id || '';
}

async function loadShopifyPrice() {
  if (!product.value) return;
  const firstVariant = colorGroups.value[0]?.variants?.[0];
  if (!firstVariant) {
    shopifyData.value = 'not_found';
    return;
  }
  try {
    const params = new URLSearchParams();
    if (firstVariant.barcode) params.set('barcode', firstVariant.barcode);
    if (firstVariant.stockCode) params.set('sku', firstVariant.stockCode);
    const res = await fetch(`/ticimax-api/shopify-urun?${params}`, {
      headers: { 'X-Panel-Key': PANEL_KEY },
    });
    if (res.status === 202) {
      shopifyData.value = null;
      return;
    }
    if (res.status === 404) {
      shopifyData.value = 'not_found';
      return;
    }
    const data = await res.json();
    shopifyData.value = data.product || 'not_found';
  } catch {
    shopifyData.value = 'not_found';
  }
}

async function loadTicimaxPrice() {
  if (!product.value) return;
  const sku = baseSku();
  if (!sku) {
    ticimaxData.value = 'not_found';
    return;
  }
  try {
    const res = await fetch(
      `/ticimax-api/ticimax-fiyat?sku=${encodeURIComponent(sku)}`,
      {
        headers: { 'X-Panel-Key': PANEL_KEY },
      }
    );
    if (res.status === 404) {
      ticimaxData.value = 'not_found';
      return;
    }
    const data = await res.json();
    ticimaxData.value = data.error ? 'not_found' : data;
  } catch {
    ticimaxData.value = 'not_found';
  }
}

async function loadModaxTrendyolPrice() {
  if (!product.value) return;
  const sku = baseSku();
  if (!sku) {
    modaxTrendyolData.value = 'not_found';
    return;
  }
  try {
    const res = await fetch(
      `/ticimax-api/modax-trendyol-fiyat?sku=${encodeURIComponent(sku)}`,
      {
        headers: { 'X-Panel-Key': PANEL_KEY },
      }
    );
    if (res.status === 404) {
      modaxTrendyolData.value = 'not_found';
      return;
    }
    const data = await res.json();
    modaxTrendyolData.value = data.error ? 'not_found' : data;
  } catch {
    modaxTrendyolData.value = 'not_found';
  }
}

async function loadData() {
  loading.value = true;
  error.value = '';
  const id = route.params.productId;
  try {
    const [prodData, maliyetRes] = await Promise.all([
      fetchMarketplaceJson(route.params.accountId, `products/${id}`),
      fetch('/ticimax-api/maliyet', { headers: { 'X-Panel-Key': PANEL_KEY } }),
    ]);
    const maliyetData = await maliyetRes.json();
    if (prodData.error) throw new Error(prodData.error);
    product.value = prodData.product;
    costs.value = maliyetData.maliyetler || {};
    if (costs.value[productKey.value]?.maliyetUsd)
      costInput.value = String(costs.value[productKey.value].maliyetUsd);
    if (colorGroups.value.length > 0)
      selectColor(colorGroups.value[0].colorCode);
    loadShopifyPrice();
    loadTicimaxPrice();
    loadModaxTrendyolPrice();
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}

onMounted(loadData);
</script>

<template>
  <div
    class="flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Top bar -->
    <div
      class="flex items-center gap-3 px-5 py-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <button
        class="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
        @click="goBack"
      >
        <span class="i-lucide-arrow-left size-4" />
        Kataloğa Dön
      </button>
      <div class="w-px h-4 bg-slate-200 dark:bg-slate-700 shrink-0" />
      <h1
        v-if="product"
        class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate flex-1 min-w-0"
      >
        {{ product.title }}
      </h1>
    </div>

    <div
      v-if="loading"
      class="flex items-center justify-center flex-1 gap-2 text-slate-400"
    >
      <span class="i-lucide-loader-2 size-5 animate-spin" />
      <span class="text-sm">Yükleniyor…</span>
    </div>

    <div v-else-if="error" class="flex items-center justify-center flex-1 p-6">
      <div
        class="flex items-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm max-w-md"
      >
        <span class="i-lucide-alert-circle size-5 shrink-0" />{{ error }}
      </div>
    </div>

    <div v-else-if="product" class="flex-1 overflow-y-auto">
      <div class="max-w-6xl mx-auto p-5 lg:p-8">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          <!-- ── Left: Gallery ─────────────────────────────────── -->
          <div class="lg:col-span-5">
            <!-- Main image — 2:3 -->
            <div
              class="aspect-[2/3] rounded-2xl overflow-hidden bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-md"
            >
              <img
                v-if="selectedImage"
                :key="selectedImage"
                :src="selectedImage"
                :alt="product.title"
                class="w-full h-full object-cover"
                @error="$event.target.style.display = 'none'"
              />
              <div
                v-else
                class="flex items-center justify-center h-full text-slate-200 dark:text-slate-600"
              >
                <span class="i-lucide-image size-20" />
              </div>
            </div>

            <!-- Thumbnail strip: ALL images for selected color -->
            <div
              v-if="currentImages.length > 1"
              class="mt-3 flex gap-2 overflow-x-auto pb-1"
            >
              <button
                v-for="img in currentImages"
                :key="img"
                class="shrink-0 w-14 aspect-[2/3] rounded-lg overflow-hidden border-2 transition-all"
                :class="
                  selectedImage === img
                    ? 'border-woot-500 shadow-md opacity-100'
                    : 'border-transparent opacity-50 hover:opacity-80'
                "
                @click="selectedImage = img"
              >
                <img
                  :src="img"
                  :alt="product.title"
                  class="w-full h-full object-cover"
                  @error="$event.target.style.display = 'none'"
                />
              </button>
            </div>
          </div>

          <!-- ── Right: Info ────────────────────────────────────── -->
          <div class="lg:col-span-7 flex flex-col gap-5">
            <!-- Name + stock -->
            <div>
              <div class="flex items-start justify-between gap-3">
                <h2
                  class="text-xl font-bold text-slate-900 dark:text-slate-100 leading-snug"
                >
                  {{ product.title }}
                </h2>
                <span
                  class="shrink-0 mt-0.5 px-2.5 py-1 rounded-full text-xs font-bold"
                  :class="
                    totalStock > 0
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                      : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
                  "
                >
                  {{ totalStock > 0 ? `${totalStock} adet` : 'Stok yok' }}
                </span>
              </div>
              <p class="text-xs text-slate-400 mt-1">
                Trendyol ID: <span class="font-mono">{{ product.id }}</span>
              </p>
            </div>

            <!-- Color selection -->
            <div v-if="colorGroups.length > 1">
              <p
                class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2.5"
              >
                Renk
                <span
                  v-if="selectedGroup"
                  class="ml-2 normal-case font-normal text-slate-700 dark:text-slate-300 tracking-normal"
                >
                  — {{ selectedGroup.colorName }}
                </span>
              </p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="group in colorGroups"
                  :key="group.colorCode"
                  class="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all"
                  :class="
                    selectedColor === group.colorCode
                      ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 shadow-sm'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-400'
                  "
                  @click="selectColor(group.colorCode)"
                >
                  <!-- Mini thumbnail from first image of this color -->
                  <img
                    v-if="group.images?.[0]"
                    :src="group.images[0]"
                    class="w-4 aspect-[2/3] rounded object-cover shrink-0"
                    @error="$event.target.style.display = 'none'"
                  />
                  {{ group.colorName }}
                  <span class="text-xs opacity-60">({{ group.images?.length || 0 }} resim)</span>
                </button>
              </div>
            </div>

            <!-- Size selection -->
            <div v-if="sizesForColor.length > 0">
              <p
                class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2.5"
              >
                Beden
              </p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="v in sizesForColor"
                  :key="v.stockCode"
                  class="min-w-[2.5rem] px-2.5 h-9 rounded-lg border text-xs font-bold transition-all"
                  :class="
                    selectedSize === v.beden
                      ? 'border-woot-500 bg-woot-500 text-white shadow'
                      : v.stok > 0
                        ? 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-400'
                        : 'border-slate-100 dark:border-slate-800 text-slate-300 dark:text-slate-600 line-through cursor-not-allowed'
                  "
                  @click="v.stok > 0 && (selectedSize = v.beden)"
                >
                  {{ v.beden }}
                </button>
              </div>
              <p
                v-if="selectedVariant"
                class="mt-2 text-xs text-slate-500 dark:text-slate-400"
              >
                Stok:
                <span
                  class="font-semibold"
                  :class="
                    selectedVariant.stok > 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-500'
                  "
                >
                  {{ selectedVariant.stok }} adet
                </span>
                · Satış:
                <span class="font-semibold text-slate-700 dark:text-slate-300">{{ selectedVariant.salePrice.toFixed(2) }} ₺</span>
                <span
                  v-if="selectedVariant.listPrice > selectedVariant.salePrice"
                  class="ml-1 line-through text-slate-400"
                >
                  {{ selectedVariant.listPrice.toFixed(2) }} ₺
                </span>
              </p>
            </div>

            <!-- ── Channel prices ──────────────────────────────── -->
            <div
              class="rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800"
            >
              <div
                class="px-4 py-3 bg-slate-50 dark:bg-slate-700/40 border-b border-slate-100 dark:border-slate-700"
              >
                <h3
                  class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest"
                >
                  Kanal Satış Fiyatları
                </h3>
              </div>
              <div class="divide-y divide-slate-100 dark:divide-slate-700">
                <!-- Trendyol Koza Butik -->
                <div class="flex items-center justify-between px-4 py-3">
                  <div class="flex items-center gap-3">
                    <span class="w-2 h-2 rounded-full bg-orange-400 shrink-0" />
                    <div>
                      <p
                        class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                      >
                        Trendyol Koza Butik
                      </p>
                      <p class="text-xs text-slate-400">Supplier #413042</p>
                    </div>
                  </div>
                  <div v-if="trendyolPrice?.sell" class="text-right">
                    <p
                      class="text-sm font-bold text-slate-900 dark:text-slate-100"
                    >
                      {{ trendyolPrice.sell }}
                    </p>
                    <p
                      v-if="trendyolPrice.list"
                      class="text-xs line-through text-slate-400"
                    >
                      {{ trendyolPrice.list }}
                    </p>
                    <p v-else class="text-xs text-emerald-500 font-medium">
                      ● Aktif
                    </p>
                  </div>
                </div>

                <!-- Trendyol ModaXLarge -->
                <div class="flex items-center justify-between px-4 py-3">
                  <div
                    class="flex items-center gap-3"
                    :class="
                      modaxTrendyolData && modaxTrendyolData !== 'not_found'
                        ? ''
                        : 'opacity-50'
                    "
                  >
                    <span class="w-2 h-2 rounded-full bg-orange-400 shrink-0" />
                    <div>
                      <p
                        class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                      >
                        Trendyol ModaXLarge
                      </p>
                      <p class="text-xs text-slate-400">Supplier #993542</p>
                    </div>
                  </div>
                  <span
                    v-if="modaxTrendyolData === null"
                    class="i-lucide-loader-2 size-4 animate-spin text-slate-400"
                  />
                  <span
                    v-else-if="modaxTrendyolData === 'not_found'"
                    class="text-xs text-slate-400"
                    >Bu kanalda yok</span>
                  <div v-else class="text-right">
                    <p
                      class="text-sm font-bold text-slate-900 dark:text-slate-100"
                    >
                      {{
                        modaxTrendyolData.minSell === modaxTrendyolData.maxSell
                          ? `${modaxTrendyolData.minSell.toFixed(2)} ₺`
                          : `${modaxTrendyolData.minSell.toFixed(2)} – ${modaxTrendyolData.maxSell.toFixed(2)} ₺`
                      }}
                    </p>
                    <p
                      v-if="
                        modaxTrendyolData.maxList &&
                        modaxTrendyolData.maxList > modaxTrendyolData.maxSell
                      "
                      class="text-xs line-through text-slate-400"
                    >
                      {{ modaxTrendyolData.maxList.toFixed(2) }} ₺
                    </p>
                    <p v-else class="text-xs text-emerald-500 font-medium">
                      ● Aktif
                    </p>
                  </div>
                </div>

                <!-- Ticimax -->
                <div class="flex items-center justify-between px-4 py-3">
                  <div
                    class="flex items-center gap-3"
                    :class="
                      ticimaxData && ticimaxData !== 'not_found'
                        ? ''
                        : 'opacity-50'
                    "
                  >
                    <span
                      class="w-2 h-2 rounded-full bg-emerald-500 shrink-0"
                    />
                    <div>
                      <p
                        class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                      >
                        Ticimax
                      </p>
                      <p class="text-xs text-slate-400">kozabutik.com</p>
                    </div>
                  </div>
                  <span
                    v-if="ticimaxData === null"
                    class="i-lucide-loader-2 size-4 animate-spin text-slate-400"
                  />
                  <span
                    v-else-if="ticimaxData === 'not_found'"
                    class="text-xs text-slate-400"
                    >Bu kanalda yok</span>
                  <div v-else class="text-right">
                    <p
                      class="text-sm font-bold text-slate-900 dark:text-slate-100"
                    >
                      {{
                        ticimaxData.minSell === ticimaxData.maxSell
                          ? `${ticimaxData.minSell.toFixed(2)} ₺`
                          : `${ticimaxData.minSell.toFixed(2)} – ${ticimaxData.maxSell.toFixed(2)} ₺`
                      }}
                    </p>
                    <p
                      v-if="
                        ticimaxData.maxList &&
                        ticimaxData.maxList > ticimaxData.maxSell
                      "
                      class="text-xs line-through text-slate-400"
                    >
                      {{ ticimaxData.maxList.toFixed(2) }} ₺
                    </p>
                    <p v-else class="text-xs text-emerald-500 font-medium">
                      ● Aktif
                    </p>
                  </div>
                </div>

                <!-- Shopify -->
                <div class="flex items-center justify-between px-4 py-3">
                  <div
                    class="flex items-center gap-3"
                    :class="
                      shopifyData && shopifyData !== 'not_found'
                        ? ''
                        : 'opacity-50'
                    "
                  >
                    <span class="w-2 h-2 rounded-full bg-green-600 shrink-0" />
                    <div>
                      <p
                        class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                      >
                        Shopify
                      </p>
                      <p class="text-xs text-slate-400">modaxlarge.com</p>
                    </div>
                  </div>
                  <span
                    v-if="shopifyData === null"
                    class="i-lucide-loader-2 size-4 animate-spin text-slate-400"
                  />
                  <span
                    v-else-if="shopifyData === 'not_found'"
                    class="text-xs text-slate-400"
                    >Bu kanalda yok</span>
                  <div v-else class="text-right">
                    <p
                      class="text-sm font-bold text-slate-900 dark:text-slate-100"
                    >
                      {{ parseFloat(shopifyData.price).toFixed(2) }} ₺
                    </p>
                    <p
                      v-if="
                        shopifyData.compare_at_price &&
                        parseFloat(shopifyData.compare_at_price) >
                          parseFloat(shopifyData.price)
                      "
                      class="text-xs line-through text-slate-400"
                    >
                      {{ parseFloat(shopifyData.compare_at_price).toFixed(2) }}
                      ₺
                    </p>
                    <p v-else class="text-xs text-emerald-500 font-medium">
                      ● Aktif
                    </p>
                  </div>
                </div>

                <!-- HB -->
                <div class="flex items-center justify-between px-4 py-3">
                  <div class="flex items-center gap-3 opacity-40">
                    <span class="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                    <div>
                      <p
                        class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                      >
                        Hepsiburada
                      </p>
                      <p class="text-xs text-slate-400">
                        Koza Butik · Entegrasyon bekleniyor
                      </p>
                    </div>
                  </div>
                  <span class="text-xs text-slate-400">—</span>
                </div>
              </div>
            </div>

            <!-- ── Cost & Profit ────────────────────────────────── -->
            <div
              class="rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800"
            >
              <div
                class="px-4 py-3 bg-slate-50 dark:bg-slate-700/40 border-b border-slate-100 dark:border-slate-700"
              >
                <h3
                  class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest"
                >
                  Maliyet & Kâr Analizi
                </h3>
              </div>
              <div class="p-4">
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2"
                >
                  Birim Alış / Üretim Maliyeti ($ · KDV hariç)
                </label>
                <div class="flex gap-2">
                  <input
                    v-model="costInput"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="örn. 5.50"
                    class="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 transition"
                    @keyup.enter="saveCost"
                  />
                  <button
                    class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all"
                    :class="
                      saved
                        ? 'bg-emerald-500 text-white'
                        : 'bg-woot-500 hover:bg-woot-600 text-white'
                    "
                    :disabled="saving"
                    @click="saveCost"
                  >
                    <span
                      v-if="saving"
                      class="i-lucide-loader-2 size-4 animate-spin"
                    />
                    <span v-else-if="saved" class="i-lucide-check size-4" />
                    <span v-else class="i-lucide-save size-4" />
                    {{ saved ? 'Kaydedildi' : 'Kaydet' }}
                  </button>
                </div>

                <div v-if="profitData" class="grid grid-cols-3 gap-3 mt-4">
                  <div
                    class="text-center p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                  >
                    <p class="text-xs text-slate-400 mb-1">Ort. Satış</p>
                    <p
                      class="text-base font-bold text-slate-800 dark:text-slate-200"
                    >
                      {{ profitData.avgPrice.toFixed(2) }} ₺
                    </p>
                  </div>
                  <div
                    class="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-xl"
                  >
                    <p class="text-xs text-slate-400 mb-1">Maliyet</p>
                    <p
                      class="text-base font-bold text-red-600 dark:text-red-400"
                    >
                      {{ profitData.cost.toFixed(2) }} ₺
                    </p>
                    <p
                      v-if="costBreakdown"
                      class="text-[10px] text-slate-400 mt-0.5"
                    >
                      {{ costBreakdown.usd.toFixed(2) }}$ × %{{
                        costBreakdown.kdvOrani
                      }}
                      KDV × {{ costBreakdown.kur }} ₺ (TCMB
                      {{ costBreakdown.kurTarihi }})
                    </p>
                  </div>
                  <div
                    class="text-center p-3 rounded-xl"
                    :class="
                      profitData.profit >= 0
                        ? 'bg-emerald-50 dark:bg-emerald-900/20'
                        : 'bg-red-50 dark:bg-red-900/20'
                    "
                  >
                    <p class="text-xs text-slate-400 mb-1">Brüt Kâr</p>
                    <p
                      class="text-base font-bold"
                      :class="
                        profitData.profit >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-600 dark:text-red-400'
                      "
                    >
                      {{ profitData.profit.toFixed(2) }} ₺
                    </p>
                    <p
                      class="text-xs font-semibold"
                      :class="
                        profitData.profit >= 0
                          ? 'text-emerald-500'
                          : 'text-red-500'
                      "
                    >
                      %{{ Math.abs(profitData.margin).toFixed(1) }}
                    </p>
                  </div>
                </div>
                <p v-else class="mt-3 text-xs text-slate-400 italic">
                  Maliyet kaydedildiğinde brüt kâr otomatik hesaplanır.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Renk × Beden stok matrisi ──────────────────────── -->
        <div
          v-if="stockMatrix"
          class="mt-8 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800"
        >
          <div
            class="px-4 py-3 bg-slate-50 dark:bg-slate-700/40 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between"
          >
            <h3
              class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest"
            >
              Stok Listesi · Renk / Beden
            </h3>
            <span class="text-xs text-slate-400"
              >Toplam {{ stockMatrix.grandTotal }} adet</span>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-xs border-collapse">
              <thead>
                <!-- Üst grup başlığı: BEDEN -->
                <tr>
                  <th
                    colspan="2"
                    class="sticky left-0 z-20 bg-slate-100 dark:bg-slate-700/60 border-b border-r border-slate-200 dark:border-slate-600 px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                  >
                    Renk
                  </th>
                  <th
                    :colspan="stockMatrix.sizes.length"
                    class="bg-slate-100 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-600 px-3 py-2 text-center font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                  >
                    Beden
                  </th>
                  <th
                    class="bg-slate-100 dark:bg-slate-700/60 border-b border-l border-slate-200 dark:border-slate-600"
                  />
                </tr>
                <!-- Alt başlık: kod, açıklama, beden sütunları, toplam -->
                <tr class="bg-slate-50 dark:bg-slate-700/30">
                  <th
                    class="sticky left-0 z-20 bg-slate-50 dark:bg-slate-700/30 border-b border-slate-200 dark:border-slate-600 px-3 py-2 text-left font-semibold text-slate-400 uppercase tracking-wider"
                  >
                    Kod
                  </th>
                  <th
                    class="sticky left-[3.25rem] z-20 bg-slate-50 dark:bg-slate-700/30 border-b border-r border-slate-200 dark:border-slate-600 px-3 py-2 text-left font-semibold text-slate-400 uppercase tracking-wider"
                  >
                    Açıklama
                  </th>
                  <th
                    v-for="(s, idx) in stockMatrix.sizes"
                    :key="s"
                    class="border-b border-r border-slate-200 dark:border-slate-600 px-3 py-2 text-center font-bold text-slate-500 dark:text-slate-300"
                    :class="colBg(idx)"
                  >
                    {{ s }}
                  </th>
                  <th
                    class="border-b border-l-2 border-slate-300 dark:border-slate-500 bg-slate-100 dark:bg-slate-700/60 px-3 py-2 text-center font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider"
                  >
                    Toplam
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in stockMatrix.rows"
                  :key="row.colorCode"
                  class="border-b border-slate-200 dark:border-slate-700 hover:bg-woot-50/40 dark:hover:bg-slate-700/20 transition-colors cursor-pointer"
                  :class="{ 'opacity-50': row.total === 0 }"
                  @click="selectColor(row.colorCode)"
                >
                  <td
                    class="sticky left-0 z-10 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 px-3 py-2 font-mono text-slate-400"
                  >
                    {{ row.colorCode }}
                  </td>
                  <td
                    class="sticky left-[3.25rem] z-10 bg-white dark:bg-slate-800 border-r-2 border-slate-300 dark:border-slate-600 px-3 py-2 font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap"
                  >
                    {{ row.colorName }}
                  </td>
                  <td
                    v-for="(s, idx) in stockMatrix.sizes"
                    :key="s"
                    class="border-r border-slate-200 dark:border-slate-700 px-3 py-2 text-center tabular-nums"
                    :class="[
                      colBg(idx),
                      row.cells[s] === 0
                        ? 'text-slate-300 dark:text-slate-600'
                        : row.cells[s] <= 2
                          ? 'font-bold text-amber-600 dark:text-amber-400'
                          : 'font-semibold text-slate-700 dark:text-slate-200',
                    ]"
                  >
                    {{ row.cells[s] || '·' }}
                  </td>
                  <td
                    class="border-l-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40 px-3 py-2 text-center font-bold tabular-nums"
                    :class="
                      row.total > 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-red-400'
                    "
                  >
                    {{ row.total }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr
                  class="bg-slate-100 dark:bg-slate-700/50 border-t-2 border-slate-300 dark:border-slate-500 font-bold"
                >
                  <td
                    colspan="2"
                    class="sticky left-0 z-10 bg-slate-100 dark:bg-slate-700/50 border-r-2 border-slate-300 dark:border-slate-600 px-3 py-2 text-slate-500 dark:text-slate-300 uppercase tracking-wider"
                  >
                    Toplam
                  </td>
                  <td
                    v-for="(s, idx) in stockMatrix.sizes"
                    :key="s"
                    class="border-r border-slate-300 dark:border-slate-600 px-3 py-2 text-center tabular-nums text-slate-700 dark:text-slate-200"
                    :class="colBg(idx)"
                  >
                    {{ stockMatrix.colTotals[s] || '·' }}
                  </td>
                  <td
                    class="border-l-2 border-slate-300 dark:border-slate-600 px-3 py-2 text-center tabular-nums text-emerald-700 dark:text-emerald-300"
                  >
                    {{ stockMatrix.grandTotal }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
